'use client'

import Link from 'next/link'
import { useCallback } from 'react'
import { useRouter } from 'next/navigation'
import { ArrowIcon } from '@/components/icons/ArrowIcon'
import { LEARN_BASE_PATH } from '@/utilities/learnBasePath'
import type { LearnCategory } from '@/payload-types'

interface Props {
  categories: LearnCategory[]
  activeSlug?: string
}

export function LearnCategorySidebar({ categories, activeSlug }: Props) {
  const router = useRouter()

  const handleSelectChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    if (value === '') {
      router.push(LEARN_BASE_PATH)
    } else if (value === 'all') {
      router.push(`${LEARN_BASE_PATH}/categories`)
    } else {
      router.push(`${LEARN_BASE_PATH}/categories/${value}`)
    }
  }, [router])

  return (
    <>
      {/* Desktop: sticky sidebar */}
      <aside className="hidden lg:block" aria-label="Category navigation">
        <div className="sticky top-24">
          <h2 className="subheading-medium mb-3">
            Featured Categories
          </h2>
          <nav className="border-t border-b border-black py-3">
            <ul className="flex flex-col" role="list">
              {categories.map((cat) => {
                const isActive = activeSlug === cat.slug
                return (
                  <li key={cat.id}>
                    <Link
                      href={`${LEARN_BASE_PATH}/categories/${cat.slug}`}
                      className={`group/link block p-3 rounded-[6px] body-medium font-medium text-black transition-colors ${
                        isActive
                          ? 'bg-civo-cloud underline'
                          : 'hover:bg-civo-cloud/50 hover:underline'
                      }`}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <span className="inline-flex items-center gap-1">
                        {cat.title}
                        <ArrowIcon className="shrink-0 size-4 opacity-0 -translate-x-1 transition-all duration-200 group-hover/link:opacity-100 group-hover/link:translate-x-0" />
                      </span>
                    </Link>
                  </li>
                )
              })}
            </ul>
          </nav>

          <Link
            href={`${LEARN_BASE_PATH}/categories`}
            className="group/link inline-flex items-center gap-1 mt-4 body-copy-medium font-medium text-black/70 transition-colors hover:text-black hover:underline"
          >
            All categories
            <ArrowIcon className="shrink-0 size-3 transition-transform duration-200 group-hover/link:translate-x-0.5" />
          </Link>
        </div>
      </aside>

      {/* Tablet/mobile: heading + native select dropdown */}
      <div className="lg:hidden flex items-center justify-between gap-4 mb-8">
        <h2 className="subheading-medium shrink-0">
          Featured Categories
        </h2>
        <div className="relative w-[200px]">
          <select
            value={activeSlug || ''}
            onChange={handleSelectChange}
            className="appearance-none w-full bg-white border border-black rounded-[4px] px-3 pr-8 py-1.5 body-copy-medium font-medium text-black cursor-pointer focus:outline-none focus:shadow-[0_0_0_1px_black]"
            aria-label="Select a category"
          >
            <option value="">Select a category</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.slug || ''}>
                {cat.title}
              </option>
            ))}
            <option value="all">All categories</option>
          </select>
          <svg
            className="absolute right-3 top-1/2 -translate-y-1/2 size-[18px] pointer-events-none"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
    </>
  )
}
